import { GuidePreference } from "./GuidePreference";
import { useEffect, useState } from "react";
import { Check, Monitor, Moon, Palette, Sun } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "./ui/popover";
import {
  type ColorThemeId,
  applyColorTheme,
  colorThemeStorageKey,
  colorThemes,
  storedColorTheme,
} from "../lib/color-theme";

type ThemePreference = "auto" | "dark" | "light";
type ResolvedTheme = Exclude<ThemePreference, "auto">;

const themeStorageKey = "chew:theme";
const darkMediaQuery = "(prefers-color-scheme: dark)";

function storedPreference(value = localStorage.getItem(themeStorageKey)): ThemePreference {
  return value === "dark" || value === "light" || value === "auto" ? value : "auto";
}

function resolvedTheme(preference: ThemePreference, systemDark = window.matchMedia(darkMediaQuery).matches): ResolvedTheme {
  return preference === "auto" ? systemDark ? "dark" : "light" : preference;
}

function applyTheme(preference: ThemePreference, resolved: ResolvedTheme) {
  const dark = resolved === "dark";
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themePreference = preference;
  document.documentElement.style.colorScheme = resolved;
  document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')?.setAttribute("content", dark ? "#181B19" : "#FFFFFF");
}

const choices: Array<{
  value: ThemePreference;
  label: string;
  description: string;
  icon: typeof Monitor;
}> = [
  { value: "auto", label: "Auto", description: "Follow your system", icon: Monitor },
  { value: "light", label: "Light", description: "Always use light", icon: Sun },
  { value: "dark", label: "Dark", description: "Always use dark", icon: Moon },
];

export function ThemeToggle() {
  const [preference, setPreference] = useState<ThemePreference>(storedPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolvedTheme(storedPreference()));
  const [colorTheme, setColorTheme] = useState<ColorThemeId>(storedColorTheme);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(darkMediaQuery);
    const sync = () => {
      const next = resolvedTheme(preference, media.matches);
      setResolved(next);
      applyTheme(preference, next);
    };
    sync();
    if (preference !== "auto") return;
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, [preference]);

  useEffect(() => {
    applyColorTheme(colorTheme);
  }, [colorTheme]);

  useEffect(() => {
    const syncAcrossTabs = (event: StorageEvent) => {
      if (event.key === themeStorageKey) setPreference(storedPreference(event.newValue));
      if (event.key === colorThemeStorageKey) setColorTheme(storedColorTheme(event.newValue));
    };
    window.addEventListener("storage", syncAcrossTabs);
    return () => window.removeEventListener("storage", syncAcrossTabs);
  }, []);

  const choose = (next: ThemePreference) => {
    localStorage.setItem(themeStorageKey, next);
    setPreference(next);
    setOpen(false);
  };

  const chooseColorTheme = (next: ColorThemeId) => {
    localStorage.setItem(colorThemeStorageKey, next);
    setColorTheme(next);
  };

  const ActiveIcon = preference === "auto" ? Monitor : preference === "dark" ? Moon : Sun;
  const label = preference === "auto" ? `Theme: Auto (system ${resolved})` : `Theme: ${preference}`;
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger render={<button aria-label={label} className="theme-toggle" title={label} type="button" />}>
        <ActiveIcon aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="end" className="theme-menu" sideOffset={7}>
        <PopoverHeader>
          <PopoverTitle>Appearance</PopoverTitle>
          <PopoverDescription>Choose how CHEW looks on this device.</PopoverDescription>
        </PopoverHeader>
        <div role="group" aria-label="Theme preference">
          {choices.map((choice) => {
            const Icon = choice.icon;
            return (
              <button aria-pressed={preference === choice.value} key={choice.value} onClick={() => choose(choice.value)} type="button">
                <Icon aria-hidden="true" />
                <span><strong>{choice.label}</strong><small>{choice.description}</small></span>
                {preference === choice.value && <Check aria-hidden="true" />}
              </button>
            );
          })}
        </div>
        <PopoverHeader>
          <PopoverTitle>Color theme</PopoverTitle>
        </PopoverHeader>
        <div role="group" aria-label="Color theme">
          {colorThemes.map((theme) => (
            <button aria-pressed={colorTheme === theme.id} key={theme.id} onClick={() => chooseColorTheme(theme.id)} type="button">
              <Palette aria-hidden="true" />
              <span><strong>{theme.label}</strong><small>{theme.description}</small></span>
              {colorTheme === theme.id && <Check aria-hidden="true" />}
            </button>
          ))}
        </div>
        <PopoverHeader><PopoverTitle>Review preferences</PopoverTitle></PopoverHeader>
        <GuidePreference />
      </PopoverContent>
    </Popover>
  );
}
